import type { AssetFieldConfig } from "@/types/layers";
import type { AssetFieldOption } from "./types";
import { linkifyText } from "@/lib/linkify";
import type { Task } from "@/types/task";

// Unified feature type for combined popup
export interface UnifiedPopupFeature {
  type: "task" | "arcgis" | "dynamic";
  title: string;
  properties: Record<string, unknown>;
  layerKey?: string;
  layerUrl?: string;
  objectId?: string | number;
  taskEnabled?: boolean;
  task?: Task; // Original task object for task features
  geometry?: import("geojson").Geometry;
}

export const extractAssetFieldOptions = (
  assetFields: AssetFieldConfig[] | null | undefined,
  properties: Record<string, unknown>
): AssetFieldOption[] => {
  if (!assetFields || assetFields.length === 0) {
    return [];
  }

  return assetFields
    .map((config) => {
      const values: string[] = [];
      // Use property_key if available, otherwise fall back to field name
      const propertyKey = config.property_key || config.field;

      // Extract values based on configuration
      if (config.extract_pattern && propertyKey) {
        // Use regex extraction if pattern is provided
        const sourceValue = String(properties[propertyKey] || "");

        try {
          // Validate regex pattern to prevent ReDoS
          if (config.extract_pattern.length > 1000) {
            console.warn("Regex pattern too long, skipping extraction");
          } else {
            const regex = new RegExp(config.extract_pattern);
            const match = sourceValue.match(regex);
            if (match && match[1]) {
              values.push(match[1]);
            }
          }
        } catch (error) {
          console.warn(
            "extractAssetFieldOptions: invalid extract_pattern",
            config.extract_pattern,
            error
          );
        }

        // Also include the raw value if different
        if (sourceValue && !values.includes(sourceValue)) {
          values.push(sourceValue);
        }
      } else if (propertyKey) {
        // Direct property extraction
        const directValue = String(properties[propertyKey] || "");
        if (directValue) {
          values.push(directValue);
        }
      }

      // Filter to unique non-empty values
      const uniqueValues = values.filter(
        (value, index, array) => value && array.indexOf(value) === index
      );

      if (uniqueValues.length === 0) {
        return null;
      }

      return {
        field: config.field,
        label: config.label,
        values: uniqueValues,
      } satisfies AssetFieldOption;
    })
    .filter((option): option is AssetFieldOption => option !== null);
};

const formatPropertyValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  // Convert URLs in the value to clickable links
  return linkifyText(String(value));
};

const extractImageFromValue = (value: unknown): string | null => {
  const candidates: string[] = [];

  if (typeof value === "string") {
    candidates.push(value);
  } else if (Array.isArray(value)) {
    value.forEach((v) => {
      if (typeof v === "string") candidates.push(v);
    });
  } else if (typeof value === "object" && value !== null) {
    const record = value as Record<string, unknown>;
    [record.url, record.href, record.src].forEach((v) => {
      if (typeof v === "string") candidates.push(v);
    });
  }

  const urlRegex = /^https?:\/\//i;
  return candidates.find((c) => urlRegex.test(c)) ?? null;
};

export const fetchArcGISAttachments = async (
  layerUrl: string,
  objectId: string | number
): Promise<string | null> => {
  if (!layerUrl || !objectId) return null;

  try {
    const attachmentUrl = `${layerUrl}/${objectId}/attachments?f=json`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const response = await fetch(attachmentUrl, {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) return null;

    const data = await response.json();
    const attachments = data.attachmentInfos || [];

    // Find first image attachment
    const imageAttachment = attachments.find((att: any) => {
      const contentType = att.contentType?.toLowerCase() || "";
      return (
        contentType.includes("image/") ||
        contentType.includes("jpg") ||
        contentType.includes("jpeg") ||
        contentType.includes("png") ||
        contentType.includes("gif") ||
        contentType.includes("webp")
      );
    });

    if (imageAttachment) {
      return `${layerUrl}/${objectId}/attachments/${imageAttachment.id}`;
    }

    return null;
  } catch (error) {
    console.debug("Failed to fetch ArcGIS attachments:", error);
    return null;
  }
};

export const buildPopupHtml = (
  title: string,
  properties: Record<string, unknown>,
  buttonId: string | null,
  taskEnabled: boolean,
  currentPage?: number,
  totalPages?: number,
  attachmentUrl?: string | null
): string => {
  // Find the first image URL: prioritize attachment, then properties
  const headerImageUrl = (() => {
    // First priority: ArcGIS attachment
    if (attachmentUrl) return attachmentUrl;

    // Second priority: image URLs in properties
    for (const [, value] of Object.entries(properties)) {
      const candidate = extractImageFromValue(value);
      if (candidate) return candidate;
    }
    return null;
  })();

  // Responsive sizing based on viewport (target tablet as smallest)
  const viewportWidth =
    typeof window !== "undefined" ? window.innerWidth : 1024;
  const isTabletOrSmall = viewportWidth <= 900; // tablet and small screens
  const isSmallLaptop = viewportWidth > 900 && viewportWidth <= 1280;
  const containerMaxWidth = isTabletOrSmall ? 320 : isSmallLaptop ? 360 : 420;
  const containerMaxHeight = isTabletOrSmall ? 360 : isSmallLaptop ? 420 : 480;
  const thumbnailHeight = isTabletOrSmall ? 140 : isSmallLaptop ? 160 : 180;
  const rows = Object.entries(properties)
    .filter(([key]) => key && key !== "OBJECTID")
    .map(([key, value]) => {
      const rowImageUrl = extractImageFromValue(value);
      const valueContent = headerImageUrl
        ? // When header thumbnail is present, avoid duplicating images in rows
          formatPropertyValue(value)
        : rowImageUrl
          ? `<div style='display:flex;flex-direction:column;align-items:flex-end;gap:6px;'>
               <img src='${rowImageUrl}' alt='Feature thumbnail' style='width:140px;height:90px;object-fit:cover;border-radius:8px;border:1px solid #e5e7eb;background:#f3f4f6;display:block;' loading='lazy' />
               <span style='font-family:"Open Sans",sans-serif;font-size:11px;color:#2563eb;word-break:break-word;'>${linkifyText(String(value))}</span>
             </div>`
          : formatPropertyValue(value);

      return `
      <div style='display:flex;align-items:center;justify-content:space-between;padding:12px 0;border-bottom:1px solid #f3f4f6;'>
        <div style='font-family:"Open Sans",sans-serif;font-weight:500;font-size:12px;color:#828282;line-height:1.3;flex:1;margin-right:16px;'>${key}</div>
        <div style='font-family:"Open Sans",sans-serif;font-weight:600;font-size:12px;color:#444444;line-height:1.5;text-align:right;min-width:80px;word-break:break-word;'>${valueContent}</div>
      </div>`;
    })
    .join("");

  const addTaskButton =
    taskEnabled && buttonId
      ? `<button id='${buttonId}' style='background:#374795;color:#ffffff;border:none;border-radius:10px;padding:12px 16px;font-family:"Open Sans",sans-serif;font-weight:600;font-size:14px;cursor:pointer;display:flex;align-items:center;gap:8px;line-height:1.4;transition:background-color 0.2s ease;'>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style="flex-shrink:0;">
            <path d="M8 3.333V12.667M3.333 8H12.667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          Add Task
        </button>`
      : "";

  const navControls =
    totalPages && totalPages > 1 && currentPage !== undefined
      ? `<div style='display:flex;align-items:center;gap:8px;'>
            <button id='arcgis-prev-page' ${currentPage === 0 ? "disabled" : ""} style='display:inline-flex;align-items:center;justify-content:center;padding:6px 8px;border:1px solid #d1d5db;border-radius:6px;background:white;color:#374151;font-family:"Open Sans",sans-serif;font-size:12px;font-weight:500;cursor:${currentPage === 0 ? "not-allowed" : "pointer"};opacity:${currentPage === 0 ? "0.5" : "1"};transition:all 0.2s;'>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style="margin-right:4px;">
                <path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Prev
            </button>
            <span style='font-family:"Open Sans",sans-serif;font-size:12px;font-weight:500;color:#6b7280;'>${currentPage + 1} / ${totalPages}</span>
            <button id='arcgis-next-page' ${currentPage === totalPages - 1 ? "disabled" : ""} style='display:inline-flex;align-items:center;justify-content:center;padding:6px 8px;border:1px solid #d1d5db;border-radius:6px;background:white;color:#374151;font-family:"Open Sans",sans-serif;font-size:12px;font-weight:500;cursor:${currentPage === totalPages - 1 ? "not-allowed" : "pointer"};opacity:${currentPage === totalPages - 1 ? "0.5" : "1"};transition:all 0.2s;'>
              Next
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style="margin-left:4px;">
                <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
         </div>`
      : "";

  // Show plain title only; pagination indicator is rendered in nav line
  const headerTitle = title;

  return `
  <div style='max-width:${containerMaxWidth}px;max-height:${containerMaxHeight}px;overflow-y:auto;overflow-x:hidden;'>
    <!-- Header Line: Title + Close -->
    <div style='display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;'>
      <div style='font-family:"Open Sans",sans-serif;font-weight:bold;font-size:18px;color:#444444;line-height:1.5;'>${headerTitle}</div>
      <button id='arcgis-close-btn' aria-label='Close' title='Close' style='display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border:1px solid #d1d5db;border-radius:50%;background:white;color:#6b7280;cursor:pointer;'>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <!-- Header Line: Navigation + Add Task -->
    <div style='display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;'>
      ${navControls}
      ${addTaskButton}
    </div>

    ${
      headerImageUrl
        ? `<div class='popup-img-container' style='margin-bottom:8px;position:relative;width:100%;height:${thumbnailHeight}px;background:#f3f4f6;border-radius:10px;border:1px solid #e5e7eb;overflow:hidden;'>
        <div class='popup-img-loader' style='position:absolute;inset:0;display:flex;align-items:center;justify-content:center;'>
          <svg style='animation:popup-spin 1s linear infinite;width:24px;height:24px;color:#9ca3af;' viewBox='0 0 24 24' fill='none'>
            <circle cx='12' cy='12' r='10' stroke='currentColor' stroke-width='3' stroke-linecap='round' style='opacity:0.25;'></circle>
            <path d='M12 2a10 10 0 0 1 10 10' stroke='currentColor' stroke-width='3' stroke-linecap='round'></path>
          </svg>
        </div>
        <a href='${headerImageUrl}' target='_blank' rel='noopener noreferrer' style='display:block;width:100%;height:100%;'>
          <img src='${headerImageUrl}' alt='Feature thumbnail' class='popup-img' style='width:100%;height:100%;object-fit:cover;opacity:0;transition:opacity 0.2s;' referrerpolicy='no-referrer' />
        </a>
      </div>
      <style>
        @keyframes popup-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      </style>`
        : ""
    }

    <div style='padding:8px 8px;'>
      ${rows || "<div style='font-family:\"Open Sans\",sans-serif;font-size:12px;color:#828282;'>No attributes available.</div>"}
    </div>
  </div>`;
};

export const applyPopupImageFallbacks = (root: HTMLElement) => {
  const img = root.querySelector<HTMLImageElement>(".popup-img");
  const loader = root.querySelector<HTMLElement>(".popup-img-loader");
  const container = root.querySelector<HTMLElement>(".popup-img-container");
  if (!img) return;

  const showImage = () => {
    img.style.opacity = "1";
    if (loader) loader.style.display = "none";
  };

  const hideImageSection = () => {
    if (container) container.style.display = "none";
    if (loader) loader.style.display = "none";
  };

  // Already loaded (cached)
  if (img.complete && img.naturalWidth > 0) {
    showImage();
    return;
  }

  img.addEventListener(
    "load",
    () => {
      showImage();
    },
    { once: true }
  );

  img.addEventListener(
    "error",
    () => {
      hideImageSection();
    },
    { once: true }
  );
};

export const attachAddTaskHandler = (
  popup: import("maplibre-gl").Popup,
  buttonId: string,
  handler: () => void
) => {
  requestAnimationFrame(() => {
    const element = popup.getElement();
    const button = element?.querySelector<HTMLButtonElement>(`#${buttonId}`);
    if (button) {
      button.addEventListener(
        "click",
        () => {
          handler();
        },
        { once: true }
      );
    }
  });
};

// Build HTML for a task feature in the unified popup
export const buildTaskPopupHtml = (
  task: Task,
  currentPage: number,
  totalPages: number
): string => {
  const viewportWidth =
    typeof window !== "undefined" ? window.innerWidth : 1024;
  const isTabletOrSmall = viewportWidth <= 900;
  const isSmallLaptop = viewportWidth > 900 && viewportWidth <= 1280;
  const containerMaxWidth = isTabletOrSmall ? 320 : isSmallLaptop ? 360 : 420;
  const containerMaxHeight = isTabletOrSmall ? 360 : isSmallLaptop ? 420 : 480;

  const priorityLabel = task.priority?.label || "No Priority";
  const priorityColor = task.priority?.color || "gray";
  const statusLabel = task.status?.label || "No Status";
  const statusColor = task.status?.color || "gray";
  const categoryLabel = task.category?.label || "No Category";

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "No date";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const navControls =
    totalPages > 1
      ? `<div style='display:flex;align-items:center;gap:8px;'>
            <button id='arcgis-prev-page' ${currentPage === 0 ? "disabled" : ""} style='display:inline-flex;align-items:center;justify-content:center;padding:6px 8px;border:1px solid #d1d5db;border-radius:6px;background:white;color:#374151;font-family:"Open Sans",sans-serif;font-size:12px;font-weight:500;cursor:${currentPage === 0 ? "not-allowed" : "pointer"};opacity:${currentPage === 0 ? "0.5" : "1"};transition:all 0.2s;'>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style="margin-right:4px;">
                <path d="M10 12L6 8L10 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Prev
            </button>
            <span style='font-family:"Open Sans",sans-serif;font-size:12px;font-weight:500;color:#6b7280;'>${currentPage + 1} / ${totalPages}</span>
            <button id='arcgis-next-page' ${currentPage === totalPages - 1 ? "disabled" : ""} style='display:inline-flex;align-items:center;justify-content:center;padding:6px 8px;border:1px solid #d1d5db;border-radius:6px;background:white;color:#374151;font-family:"Open Sans",sans-serif;font-size:12px;font-weight:500;cursor:${currentPage === totalPages - 1 ? "not-allowed" : "pointer"};opacity:${currentPage === totalPages - 1 ? "0.5" : "1"};transition:all 0.2s;'>
              Next
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style="margin-left:4px;">
                <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
         </div>`
      : "";

  return `
  <div style='max-width:${containerMaxWidth}px;max-height:${containerMaxHeight}px;overflow-y:auto;overflow-x:hidden;'>
    <!-- Header Line: Title + Close -->
    <div style='display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;'>
      <div style='display:flex;align-items:center;gap:8px;'>
        <span style='background:#3b82f6;color:white;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:600;'>TASK</span>
        <div style='font-family:"Open Sans",sans-serif;font-weight:bold;font-size:16px;color:#444444;line-height:1.5;'>${task.title || "Untitled Task"}</div>
      </div>
      <button id='arcgis-close-btn' aria-label='Close' title='Close' style='display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border:1px solid #d1d5db;border-radius:50%;background:white;color:#6b7280;cursor:pointer;'>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <!-- Navigation + View Task Button -->
    <div style='display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;'>
      ${navControls}
      <button id='view-task-btn' style='background:#374795;color:#ffffff;border:none;border-radius:10px;padding:10px 14px;font-family:"Open Sans",sans-serif;font-weight:600;font-size:13px;cursor:pointer;display:flex;align-items:center;gap:6px;transition:background-color 0.2s ease;'>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d="M8 3C4.5 3 1.5 6 1.5 8s3 5 6.5 5 6.5-3 6.5-5-3-5-6.5-5z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          <circle cx="8" cy="8" r="2" stroke="currentColor" stroke-width="1.5"/>
        </svg>
        View Task
      </button>
    </div>

    <!-- Task Details -->
    <div style='padding:0 4px;'>
      <!-- Status & Priority Row -->
      <div style='display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap;'>
        <span style='background:${statusColor === "gray" ? "#f3f4f6" : `var(--color-${statusColor}-100, #dbeafe)`};color:${statusColor === "gray" ? "#6b7280" : `var(--color-${statusColor}-800, #1e40af)`};padding:4px 10px;border-radius:6px;font-size:11px;font-weight:600;'>${statusLabel}</span>
        <span style='background:${priorityColor === "gray" ? "#f3f4f6" : `var(--color-${priorityColor}-100, #fee2e2)`};color:${priorityColor === "gray" ? "#6b7280" : `var(--color-${priorityColor}-800, #991b1b)`};padding:4px 10px;border-radius:6px;font-size:11px;font-weight:600;'>${priorityLabel}</span>
        <span style='background:#f3f4f6;color:#6b7280;padding:4px 10px;border-radius:6px;font-size:11px;font-weight:500;'>${categoryLabel}</span>
      </div>

      <!-- Info Rows -->
      <div style='display:flex;flex-direction:column;gap:8px;'>
        ${task.description ? `<div style='font-family:"Open Sans",sans-serif;font-size:12px;color:#6b7280;line-height:1.5;padding:8px;background:#f9fafb;border-radius:6px;'>${task.description}</div>` : ""}
        
        <div style='display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid #f3f4f6;'>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style="color:#9ca3af;">
            <rect x="2" y="3" width="12" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/>
            <path d="M2 7h12M5 1v4M11 1v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          <span style='font-family:"Open Sans",sans-serif;font-size:12px;color:#828282;'>Due:</span>
          <span style='font-family:"Open Sans",sans-serif;font-size:12px;font-weight:600;color:#444444;'>${formatDate(task.due_date)}</span>
        </div>

        ${
          task.layer_name
            ? `<div style='display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid #f3f4f6;'>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style="color:#9ca3af;">
            <path d="M8 1L1 5l7 4 7-4-7-4z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
            <path d="M1 8l7 4 7-4M1 11l7 4 7-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span style='font-family:"Open Sans",sans-serif;font-size:12px;color:#828282;'>Layer:</span>
          <span style='font-family:"Open Sans",sans-serif;font-size:12px;font-weight:600;color:#444444;'>${task.layer_name}</span>
        </div>`
            : ""
        }

        ${
          task.asset_id
            ? `<div style='display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid #f3f4f6;'>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style="color:#9ca3af;">
            <path d="M2 4h12M2 8h12M2 12h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          <span style='font-family:"Open Sans",sans-serif;font-size:12px;color:#828282;'>Asset:</span>
          <span style='font-family:"Open Sans",sans-serif;font-size:12px;font-weight:600;color:#444444;'>${task.asset_id}</span>
        </div>`
            : ""
        }
      </div>
    </div>
  </div>`;
};

// Build unified popup for combined features from all layers
export const buildUnifiedPopupHtml = (
  feature: UnifiedPopupFeature,
  currentPage: number,
  totalPages: number,
  attachmentUrl?: string | null
): string => {
  // For task features, use special task popup
  if (feature.type === "task" && feature.task) {
    return buildTaskPopupHtml(feature.task, currentPage, totalPages);
  }

  // For ArcGIS/dynamic features, use regular popup with layer type badge
  const typeBadge =
    feature.type === "dynamic"
      ? "<span style='background:#10b981;color:white;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:600;margin-right:8px;'>DYNAMIC</span>"
      : "<span style='background:#8b5cf6;color:white;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:600;margin-right:8px;'>FEATURE</span>";

  return buildPopupHtml(
    typeBadge + feature.title,
    feature.properties,
    feature.taskEnabled ? `add-task-btn-${Date.now()}` : null,
    feature.taskEnabled || false,
    currentPage,
    totalPages,
    attachmentUrl
  );
};
